import type { Event, EventHint } from '@sentry/nextjs';

// React hydration errors: minified codes (418/425 = mismatch, 422/423 = client-render fallback)
// plus the unminified dev wording, so the tag works on any build.
const HYDRATION_ERROR_PATTERN =
  /Minified React error #(418|422|423|425)|Hydration failed|error while hydrating|Text content does not match/;

/**
 * make React hydration errors diagnosable in production Sentry.
 *
 * - Tags hydration errors (`hydration_error: true`) so they can be searched/alerted on.
 * - Lifts `componentStack` — attached to recoverable errors by our next.js patch
 *   (patches/next@14.2.35.patch) — into the event's `react` context, since Sentry's onerror
 *   handler drops non-standard properties of the thrown error.
 *
 * Typed against the general `Event`, which is what Sentry hands every processor and what
 * `addEventProcessor` requires. Non-error events carry no `exception` and no `componentStack`, so
 * they fall through untouched.
 */
export function hydrationErrorEventProcessor(event: Event, hint: EventHint): Event {
  const message = event.exception?.values?.[0]?.value ?? '';

  if (HYDRATION_ERROR_PATTERN.test(message)) {
    event.tags = { ...event.tags, hydration_error: true };
  }

  const originalException = hint.originalException;
  const componentStack =
    originalException && typeof originalException === 'object'
      ? (originalException as { componentStack?: unknown }).componentStack
      : undefined;

  if (typeof componentStack === 'string' && componentStack.length > 0) {
    event.contexts = {
      ...event.contexts,
      react: { ...(event.contexts?.react ?? {}), componentStack },
    };
  }

  return event;
}
